<template>
  <div class="tw:h-[350px] tw:w-full">
    <canvas id="chartElement" ref="chartElement"></canvas>
  </div>
</template>

<script setup lang="ts">
import {
  Chart,
  Filler,
  CategoryScale,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  Tooltip,
  ChartDataset,
} from "chart.js";
import { onMounted, ref, watch } from "vue";

interface Props {
  labels: string[];
  datasets: ChartDataset<"line">[];
}

const props = defineProps<Props>();

Chart.register(CategoryScale, Filler, LineController, LineElement, PointElement, LinearScale, Tooltip);

const chartElement = ref<HTMLCanvasElement | null>(null);
const chart = ref();

function initChart() {
  if (!chartElement.value) {
    return;
  }

  chart.value = new Chart(chartElement.value, {
    type: "line",
    options: {
      plugins: {
        legend: {
          display: false,
        },
      },
      elements: {
        point: {
          radius: 0,
        },
        line: {
          tension: 0,
        },
      },
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 500,
        easing: "easeInQuad",
      },
      transitions: {
        active: {
          animation: {
            duration: 0,
          },
        },
      },
      interaction: {
        intersect: false,
        mode: "index",
      },
      scales: {
        x: {
          stacked: false,
          title: {
            display: true,
            text: "",
          },
          grid: {
            display: false,
          },
          offset: false,
        },
        y: {
          beginAtZero: true,
          suggestedMin: 0,
          min: 0,
          offset: false,
          stacked: false,
          title: {
            display: true,
            text: "",
          },
          grid: {
            display: true,
          },
          ticks: {
            maxTicksLimit: 5,
            stepSize: 1,
          },
        },
      },
    },
    data: {
      labels: props.labels,
      datasets: props.datasets,
    },
  });
}

onMounted(() => {
  initChart();
});

watch(
  () => [props.labels, props.datasets],
  ([newLabels, newDatasets]) => {
    if (chart.value) {
      chart.value.data.labels = newLabels;
      chart.value.data.datasets = newDatasets;
      if (chart.value) {
        (chart.value as Chart).destroy();
        initChart();
      }
    }
  },
  { deep: true }
);
</script>
